import { MakerJSDownload, MimeType } from "@project/emerge";
import maxBy from "lodash/maxBy";
import MakerJS from "makerjs";
import { MaxRectsPacker } from "maxrects-packer";
import { PaddingStyle } from "../enum/PaddingStyle";
import type { ExtendedCutoutDefinition } from "../interfaces/CutoutProperties";
import type { PadDefinition } from "../interfaces/PadProperties";
import { RectPropertiesBySide } from "../structure/RectProperties";

const PADDING_STYLE_NAMES: Record<PaddingStyle, string> = {
  [PaddingStyle.EGGBOX]: "Noppen",
  [PaddingStyle.SLOTTED]: "Nuten",
  [PaddingStyle.SOFT]: "weich",
  [PaddingStyle.STANDARD]: "",
};

/** Same nesting margin as panel DXF (tool + clearance). */
const PACKING_PADDING = 11 / 2;

const BIN_GAP = 30;

/** Default foam sheet size [mm] when no per-material board size exists. */
const BOARD_SIZE: [number, number] = [1250, 2500];

/**
 * Cutouts that belong on the foam DXF: build-step edge cutouts and the
 * supply-part holes of options with `applyFoamCutouts` (e.g. Einbau-Eckrollen).
 * Other supply-part holes on pads are for 3D rendering only; the foam is glued
 * over those parts in manufacturing.
 */
export function padCutoutsForDxf(
  cutouts: ExtendedCutoutDefinition[] | undefined,
): ExtendedCutoutDefinition[] {
  return (cutouts ?? []).filter(
    (cutout) => cutout.interiorEdge === true || cutout.manufacturing === true,
  );
}

interface PadData {
  pad: PadDefinition;
  originalWidth: number;
  originalHeight: number;
}

/**
 * Creates a DXF download for foam padding/pads
 * Uses MaxRects bin-packing and cutout subtraction like {@link PanelDownloadGenerator}
 */
export class PadDownloadGenerator {
  private readonly pads: PadDefinition[];
  private readonly title: string;

  constructor(pads: PadDefinition[], title: string) {
    if (pads.length === 0) {
      throw new Error(
        "Cannot create PadDownloadGenerator with empty pads array",
      );
    }
    this.pads = pads;
    this.title = title;
  }

  public async generateDownload(): Promise<PadMakerJSDownload> {
    const firstPad = this.pads[0];
    const styleName =
      PADDING_STYLE_NAMES[firstPad?.style ?? PaddingStyle.STANDARD];
    const generatedTitle = `${firstPad?.strength ?? 0} ${styleName}`.trim();

    return new PadMakerJSDownload(
      generatedTitle || this.title,
      MimeType.DXF,
      this.pads,
    );
  }
}

/**
 * MakerJS DXF for pads: MaxRects packing + cutouts (same pipeline as panels).
 */
class PadMakerJSDownload extends MakerJSDownload {
  private readonly pads: PadDefinition[];

  constructor(
    title: string,
    mimeType: MimeType.DXF | MimeType.SVG,
    pads: PadDefinition[],
  ) {
    super(title, mimeType);
    this.pads = pads;
    this.packPads();
  }

  private packPads(): void {
    const firstPad = this.pads[0];
    if (!firstPad) return;

    const { packer, boardHeight } = this.createPacker();

    for (const pad of this.pads) {
      packer.add(
        pad.size[0] + PACKING_PADDING * 2,
        pad.size[1] + PACKING_PADDING * 2,
        {
          pad,
          originalWidth: pad.size[0],
          originalHeight: pad.size[1],
        } as PadData,
      );
    }

    let layerNumber = 0;
    for (let binIndex = 0; binIndex < packer.bins.length; binIndex++) {
      const bin = packer.bins[binIndex];
      if (!bin) continue;

      const binOffsetY =
        binIndex === 0 ? 0 : -(boardHeight + BIN_GAP) * binIndex;

      for (const rect of bin.rects) {
        layerNumber++;
        this.placePad(rect, binIndex, layerNumber, binOffsetY);
      }
    }
  }

  private createPacker() {
    const boardSize = BOARD_SIZE;
    const maxWidth = maxBy(this.pads, (p) => p.size[0])?.size[0] ?? 0;
    const boardW = boardSize[0] ?? 1250;
    const boardH = boardSize[1] ?? 2500;
    const w = maxWidth > boardW ? boardH : boardW;
    const h = maxWidth > boardW ? boardW : boardH;

    const packer = new MaxRectsPacker(w, h, PACKING_PADDING, {
      smart: true,
      pot: false,
      square: false,
      allowRotation: true,
      tag: false,
      border: 0,
    });

    return { packer, boardHeight: h };
  }

  private placePad(
    rect: { x: number; y: number; rot: boolean; data: unknown },
    binIndex: number,
    layerNumber: number,
    binOffsetY: number,
  ): void {
    const padData = rect.data as PadData;
    const pad = padData.pad;
    const isRotated = rect.rot;

    const padModel = this.createPadModel(pad);
    const subModel = MakerJS.model.center(
      MakerJS.model.mirror(padModel, false, true),
    );

    subModel.layer = `${layerNumber} ${pad.size[0]}mm x ${pad.size[1]}mm (Platte ${binIndex + 1})`;

    if (isRotated) {
      MakerJS.model.rotate(subModel, 90);
    }

    const rectX = rect.x + PACKING_PADDING;
    const rectY = rect.y + PACKING_PADDING + binOffsetY;
    const halfW = isRotated ? pad.size[1] / 2 : pad.size[0] / 2;
    const halfH = isRotated ? pad.size[0] / 2 : pad.size[1] / 2;

    this.model.addSubModel(subModel, rectX + halfW, rectY + halfH);
  }

  /**
   * Single pad footprint with manufacturing cutouts (boolean subtraction).
   * 3D-only supply-part pad holes are omitted; see {@link padCutoutsForDxf}.
   */
  private createPadModel(pad: PadDefinition): MakerJS.IModel {
    const { side, size, position } = pad;
    const cutouts = padCutoutsForDxf(pad.cutouts);
    const width = size[0];
    const height = size[1];

    let result: MakerJS.IModel = new MakerJS.models.Rectangle(width, height);

    const rectProps = RectPropertiesBySide[side];
    if (!rectProps) {
      return MakerJS.model.moveRelative(result, [-width / 2, -height / 2]);
    }

    const { horizontal, horizontalDir, vertical, verticalDir } = rectProps;

    for (const cutout of cutouts ?? []) {
      if (!cutout.path) continue;

      try {
        const dx =
          (cutout.position[horizontal] - position[horizontal]) *
            horizontalDir[horizontal] +
          width / 2;
        const dy =
          (-cutout.position[vertical] + position[vertical]) *
            verticalDir[vertical] +
          height / 2;

        let contourModel = MakerJS.importer.fromSVGPathData(cutout.path);
        contourModel = MakerJS.model.mirror(contourModel, false, true);
        contourModel = MakerJS.model.scale(contourModel, 1, true);

        if (cutout.orientation && cutout.orientation !== 0) {
          contourModel = MakerJS.model.rotate(
            contourModel,
            cutout.orientation,
            [0, 0],
          );
        }

        contourModel = MakerJS.model.moveRelative(contourModel, [dx, dy]);
        result = MakerJS.model.combineSubtraction(result, contourModel);
      } catch (error) {
        console.warn("Failed to process pad cutout:", cutout.path, error);
      }
    }

    return MakerJS.model.moveRelative(result, [-width / 2, -height / 2]);
  }
}

export function getPaddingStyleName(style: PaddingStyle): string {
  return PADDING_STYLE_NAMES[style] || "";
}
